import { User, Sparkles, Copy, Check } from 'lucide-react';
import { useCallback } from 'react';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';
import ToolProgressStepper from './ToolProgressStepper';
import useCopyState from './useCopyState';
import CitationPillText from './CitationPillText';
import { useRespondentMap } from './useRespondentMap';
import type { ChatMessage } from './useResearchChat';

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
  /** All artifacts from the thread — used as fallback when message.artifacts
   *  is not populated (loaded threads vs streaming). */
  allArtifacts?: ResearchArtifact[];
  /** Called when the user clicks "Continue" on a truncated message. */
  onContinue?: () => void;
  /**
   * Project id, threaded down so `useRespondentMap` can fetch the
   * project-level respondent_index → conversation_id lookup. Without
   * this the chat-bubble citation pills fall back to artifact-only
   * scanning, which mis-disables `[R12]` pills for respondents not
   * cited in any quote_collection in the current thread.
   */
  projectId?: string;
}

function MessageBubble({
  message,
  isStreaming,
  allArtifacts,
  onContinue,
  projectId,
}: MessageBubbleProps) {
  const { lang } = useTranslation();
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(message.content)
      .then(() => triggerCopied())
      .catch(() => {});
  }, [message.content, triggerCopied]);

  const isUser = message.role === 'user';

  // Build the shared respondent → conversation map. Two layers:
  //   (1) project-level lookup (every respondent in the project) so
  //       `[R12]` pills are clickable even when the thread's
  //       artifacts don't carry that respondent's conversation_id,
  //   (2) artifact-scanned overlay so quote_collections that carry
  //       richer scroll-target metadata (message_id, question_id)
  //       win where available.
  //
  // The same hook + map shape is used by the artifact-panel
  // renderers (theme_summary, markdown_report) so a `[R12]` pill
  // appears identically and clicks identically wherever it shows up.
  const respondentMap = useRespondentMap(
    message.artifacts?.length ? message.artifacts : allArtifacts,
    projectId,
  );

  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="flex items-start gap-2.5 max-w-[80%]">
          <div className="bg-primary/5 rounded-2xl rounded-tr-sm px-4 py-2.5">
            <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">
              {message.content}
            </p>
          </div>
          <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 shrink-0 mt-0.5">
            <User className="h-3.5 w-3.5 text-primary" />
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex mb-4 group">
      <div className="flex items-start gap-2.5 max-w-[85%]">
        <div className="flex items-center justify-center h-7 w-7 rounded-full bg-neutral-100 shrink-0 mt-0.5">
          <Sparkles className="h-3.5 w-3.5 text-neutral-500" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Tool progress — archival "X tools used" line, only shown
              AFTER streaming finishes. While streaming is in progress
              the live progress stepper is rendered in a pinned bar at
              the bottom of ChatThread (above the input) so it stays
              visible regardless of the message bubble's scroll
              position. */}
          {!isStreaming && message.tool_steps && message.tool_steps.length > 0 && (
            <ToolProgressStepper steps={message.tool_steps} collapsed />
          )}

          {/* Message content */}
          {message.content && (
            <div
              className="text-sm text-neutral-800 leading-relaxed"
              aria-live={isStreaming ? 'polite' : undefined}
            >
              <CitationPillText
                text={message.content}
                respondentMap={respondentMap}
                variant="inline"
              />
              {isStreaming && (
                <span className="inline-block w-1.5 h-4 bg-neutral-400 animate-pulse ml-0.5 align-text-bottom" />
              )}
            </div>
          )}

          {/* Truncation indicator + Continue button */}
          {!isStreaming && message.truncation_reason && (
            <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <span className="text-xs text-amber-700">
                {message.truncation_reason === 'max_tokens'
                  ? lang.get('msg.truncation.maxTokens')
                  : lang.get('msg.truncation.connectionClosed')}
              </span>
              {onContinue && (
                <button
                  type="button"
                  onClick={onContinue}
                  className="text-xs font-medium text-primary hover:text-primary underline underline-offset-2"
                >
                  {lang.get('msg.truncation.continue')}
                </button>
              )}
            </div>
          )}

          {/* Artifact references */}
          {message.artifacts && message.artifacts.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {message.artifacts.map((artifact) => (
                <span
                  key={artifact.artifact_id}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-primary bg-primary/5 px-2 py-1 rounded-md"
                >
                  <Sparkles className="h-3 w-3" />
                  {artifact.title} added
                </span>
              ))}
            </div>
          )}

          {/* Actions — visible on hover OR keyboard focus.
              Sprint A audit fix: previously the wrapper was opacity-0 +
              group-hover:opacity-100 with no focus-within fallback, so a
              keyboard user who Tab'd to the (invisible) copy button saw
              nothing — a WCAG 2.4.7 focus-visible failure. Added
              focus-within:opacity-100 so the button reveals itself when
              keyboard focus reaches it. */}
          {!isStreaming && message.content && (
            <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={handleCopy}
                className={cn(
                  'p-2 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                  copied ? 'text-primary' : 'text-neutral-500',
                )}
                title={copied ? 'Copied' : 'Copy message'}
                aria-label={copied ? 'Message copied' : 'Copy message'}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageBubble;
